// Revenue triage — inline searchable cell editor for the Property and
// Category columns (QBO-style categorize-in-place). Popover + Command picker;
// the owning tab supplies options and the PATCH mutation via onSelect.

import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronDown } from "lucide-react";

export interface InlineCellOption {
  value: string;
  label: string;
}

export function InlineCellEditor({
  value,
  displayLabel,
  placeholder,
  options,
  onSelect,
  testId,
  disabled,
  widthClass = "w-56",
}: {
  /** Current option value ("" / null = unset). */
  value: string | null;
  /** What the closed cell shows; defaults to the matching option label. */
  displayLabel?: string | null;
  /** Muted text shown when unset, e.g. "Add property". */
  placeholder: string;
  options: InlineCellOption[];
  onSelect: (value: string) => void;
  testId: string;
  disabled?: boolean;
  widthClass?: string;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === (value ?? ""));
  const label = displayLabel ?? current?.label ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`group inline-flex max-w-full items-center gap-1 rounded px-1.5 py-0.5 text-sm text-left hover:bg-gray-100 disabled:pointer-events-none ${
            label ? "text-gray-900" : "text-gray-400 italic"
          }`}
          data-testid={testId}
        >
          <span className="truncate">{label ?? placeholder}</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-gray-400 opacity-0 group-hover:opacity-100" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent className={`${widthClass} p-0`} align="start">
        <Command>
          <CommandInput placeholder={`Search…`} data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.label}
                  onSelect={() => {
                    setOpen(false);
                    if (o.value !== (value ?? "")) onSelect(o.value);
                  }}
                  data-testid={`${testId}-option-${o.value}`}
                >
                  <Check
                    className={`mr-2 h-3.5 w-3.5 ${o.value === (value ?? "") ? "opacity-100" : "opacity-0"}`}
                    aria-hidden="true"
                  />
                  <span className="truncate">{o.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
